/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import { createClient } from 'graphql-ws';
import { useEffect, useReducer, useState } from 'react';
import ConfigManager from '../../helper/ConfigManager';
import { getLocale, useLocale } from '../../languages/LocaleProvider';

const createClientWithToken = (token, locale) =>
	createClient({
		url: `${ConfigManager.config.gatewayClientConfig.socketEndpointURL.replace(
			'/machineserver-proxy',
			'/graphql'
		)}?authToken=${token}`,
		connectionParams: { id: Math.random().toString(), locale }
	});

const gqlClient = {
	client: createClientWithToken(localStorage.getItem('jwtToken')),
	token: localStorage.getItem('jwtToken'),
	locale: null
};
export const getGQLClient = ({ locale }) => {
	const currentToken = localStorage.getItem('jwtToken');
	if (gqlClient.token !== currentToken || locale !== gqlClient.locale) {
		gqlClient.client = createClientWithToken(currentToken, locale);
		gqlClient.token = currentToken;
		gqlClient.locale = locale;
	}
	return gqlClient.client;
};

export const runQuery = async (query, variables, operationName) =>
	new Promise((resolve, reject) =>
		getGQLClient({ locale: getLocale() }).subscribe(
			{ query, variables, operationName },
			{
				next: (data) => {
					if (data.data) {
						resolve(data.data);
					} else if (data.errors) {
						reject(data.errors);
					}
				},
				// eslint-disable-next-line
				error: (error) => reject([error.reason ? error.reason : error]),
				complete: () => {}
			}
		)
	);

export const useQuery = (query, variables = {}, operationName) => {
	const [requestState, dispatch] = useReducer(
		(state, action) => {
			switch (action.type) {
				case 'fetch':
					return { status: 'fetching', result: null };
				case 'error':
					return { status: 'error', result: action.data };
				case 'data':
					return { ...state, result: action.data };
				case 'done': {
					return state.result
						? { ...state, status: state.status !== 'error' ? 'done' : state.status }
						: state;
				}
				default:
					throw new Error(`Unknown action type: ${action.type}`);
			}
		},
		{
			status: 'not-fechted',
			result: null
		}
	);
	const { locale } = useLocale();

	useEffect(() => {
		dispatch({ type: 'fetch' });
		const unsubscribe = getGQLClient({ locale }).subscribe(
			{ query, variables, operationName },
			{
				next: (data) => {
					if (data.errors) {
						dispatch({ type: 'error', data: data.errors });
					} else if (data.data) {
						dispatch({ type: 'data', data: data.data });
					}
				},
				error: (error) => {
					dispatch({ type: 'error', data: [error.reason ? error.reason : error] });
				},
				complete: () => dispatch({ type: 'done' })
			}
		);
		return () => {
			unsubscribe();
		};
	}, [query, operationName, locale, ...Object.values(variables)]);
	return requestState;
};

export const useLocalState = (remoteValue) => {
	const [remote, setRemote] = useState(remoteValue);
	const [local, setLocal] = useState(null);
	useEffect(() => {
		const prev = local;
		setRemote(remoteValue);
		setTimeout(() => {
			setLocal((current) => (current === prev ? null : current));
		}, 200);
	}, [remoteValue]);
	return [local !== null ? local : remote, setLocal, setRemote, local !== null && local !== remote];
};

export const useSubscription = (query, variables = {}, operationName) => {
	const [result, setResult] = useState([null, null]);
	const { locale } = useLocale();
	useEffect(() => {
		return getGQLClient({ locale }).subscribe(
			{ query, variables, operationName },
			{
				next: (data) => {
					setResult([data.data, data.errors]);
				},
				error: (error) =>
					setResult([
						null,
						Array.isArray(error) ? error : [error.reason ? { ...error, message: error.reason } : error]
					]),
				complete: () => {}
			}
		);
	}, [query, operationName, locale, ...Object.values(variables)]);
	return result;
};
